/**
 * ReportUserModal — reports a member, mirroring mobile's ReportUser screen:
 * multi-select reasons plus free text when "Other" is picked.
 *
 * The report is keyed by profile GUID, not the numeric user id, because that is
 * what POST /UserProfile/ReportUser expects.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import profileService from '@wlb/shared/api/services/profileService';
import {
  OTHER_REPORT_REASON_ID,
  REPORT_REASON_OPTIONS,
} from '@wlb/shared/api/services/reportService';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import '../feed/feedModal.css';

interface ReportUserModalProps {
  /** Profile GUID of the member being reported. */
  userGuid: string;
  onClose: () => void;
}

export function ReportUserModal({ userGuid, onClose }: ReportUserModalProps) {
  const { t } = useTranslation();
  const [selectedReasonIds, setSelectedReasonIds] = useState<number[]>([]);
  const [otherDetails, setOtherDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const isOtherSelected = useMemo(
    () => selectedReasonIds.includes(OTHER_REPORT_REASON_ID),
    [selectedReasonIds],
  );
  const disabled =
    submitting ||
    selectedReasonIds.length === 0 ||
    (isOtherSelected && !otherDetails.trim());

  const toggleReason = (reasonId: number) => {
    setSelectedReasonIds(current =>
      current.includes(reasonId)
        ? current.filter(id => id !== reasonId)
        : [...current, reasonId],
    );
  };

  const handleSubmit = async () => {
    if (disabled) return;
    setSubmitting(true);
    try {
      const response = await profileService.reportUser({
        reportedUserGuid: userGuid,
        // "Other" is carried by the free text, not as a violation code.
        violationTypes: selectedReasonIds.filter(id => id !== OTHER_REPORT_REASON_ID),
        otherDescription: isOtherSelected ? otherDetails.trim() : '',
      });
      if (response.data?.success === false) throw new Error('report_user_failed');
      setSubmitted(true);
    } catch {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Modal
        title={t('profile.report.title')}
        onClose={onClose}
        labelledBy="report-user-success-title"
        className="report-post-modal"
      >
        <div className="report-post-modal__success">
          <div className="report-post-modal__success-icon" aria-hidden="true">✓</div>
          <h3>{t('profile.report.thankYou.title')}</h3>
          <p>{t('profile.report.thankYou.subtitle')}</p>
          <Button fullWidth size="lg" onClick={onClose}>
            {t('common.buttons.done')}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t('profile.report.title')}
      onClose={onClose}
      labelledBy="report-user-title"
      className="report-post-modal"
    >
      <div className="report-post-modal__content">
        <div className="report-post-modal__intro">
          <p className="report-post-modal__heading">{t('profile.report.steps.whatReporting')}</p>
          <p className="report-post-modal__subheading">
            {t('profile.report.steps.selectAllThatApply')}
          </p>
        </div>
        <div
          className="report-post-modal__reasons"
          role="group"
          aria-label={t('profile.report.steps.whatReporting')}
        >
          {REPORT_REASON_OPTIONS.map(reason => {
            const isSelected = selectedReasonIds.includes(reason.id);
            const isOther = reason.id === OTHER_REPORT_REASON_ID;
            return (
              <div key={reason.id} className="report-post-modal__reason-wrap">
                <button
                  type="button"
                  className={`report-post-modal__reason${isSelected ? ' report-post-modal__reason--selected' : ''}`}
                  aria-checked={isSelected}
                  role="checkbox"
                  onClick={() => toggleReason(reason.id)}
                >
                  <span className="report-post-modal__reason-indicator" aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                  <span className="report-post-modal__reason-label">{t(reason.labelKey)}</span>
                </button>
                {isOther && isSelected && (
                  <textarea
                    className="report-post-modal__details"
                    value={otherDetails}
                    onChange={event => setOtherDetails(event.target.value)}
                    placeholder={t('profile.report.detailsPlaceholder')}
                    rows={4}
                  />
                )}
              </div>
            );
          })}
        </div>
        <Button
          fullWidth
          size="lg"
          onClick={() => { handleSubmit().catch(() => undefined); }}
          disabled={disabled}
        >
          {t('profile.report.buttons.report')}
        </Button>
      </div>
    </Modal>
  );
}
